// B3.7 — first-run discovery offer eligibility (pure).

import type { AuthContextValue } from "@/contexts/AuthContext";
import type { PrimaryMode } from "@/components/lead/sections/LeadWorkspaceHeaderAccess";
import type { ActivityDockAssistLevel } from "@/lib/activityDock/assistLevel";
import type { HasCapabilityFn } from "@/lib/capabilities/capabilities";
import { SALES_REP_ROLES } from "@/lib/roles/turboSalesRoles";
import { getBucketForMode } from "@/components/shell/modes/bucketsRegistry";
import type { FireflyTourArtifact } from "./tourTypes";
import type { CanAccessPrimaryModeFn } from "./resolveFireflyTour";
import { resolveFireflyTour } from "./resolveFireflyTour";
import type { FireflyDiscoveryModeConfig } from "./fireflyDiscoveryConfig";

export { FIREFLY_DISCOVERY_DWELL_MS } from "./fireflyDiscoveryConfig";

export type EvaluateFireflyDiscoveryOfferInput = {
  auth: AuthContextValue;
  currentMode: PrimaryMode;
  assistLevel: ActivityDockAssistLevel;
  discoveryConfig: FireflyDiscoveryModeConfig | null;
  artifact: FireflyTourArtifact;
  canAccessPrimaryMode: CanAccessPrimaryModeFn;
  modeEnteredAtMs: number;
  nowMs: number;
  dismissedPersistently: boolean;
  dismissedThisSession: boolean;
  tourCompleted: boolean;
  /** When omitted, role-derived capability defaults apply. */
  hasCapability?: HasCapabilityFn;
};

export function evaluateFireflyDiscoveryOffer(
  input: EvaluateFireflyDiscoveryOfferInput,
): boolean {
  if (!input.discoveryConfig) return false;
  if (input.discoveryConfig.tourId !== input.artifact.id) return false;
  if (input.assistLevel === "off" || input.assistLevel === "minimal") return false;
  if (input.dismissedPersistently || input.dismissedThisSession || input.tourCompleted) {
    return false;
  }
  if (input.auth.status !== "ready") return false;
  if (!input.auth.hasRole(...SALES_REP_ROLES)) return false;
  if (getBucketForMode(input.currentMode) !== "sales") return false;
  if (input.nowMs - input.modeEnteredAtMs < input.discoveryConfig.dwellMs) return false;

  const resolution = resolveFireflyTour({
    tourId: input.artifact.id,
    artifacts: [input.artifact],
    auth: input.auth,
    currentMode: input.currentMode,
    canAccessPrimaryMode: input.canAccessPrimaryMode,
    hasCapability: input.hasCapability,
  });

  return resolution.kind === "start";
}
